import { useState, useCallback, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  IconButton,
  Checkbox,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  List,
  ListItem,
} from '@mui/material'
import {
  CloudUpload,
  CreateNewFolder,
  Delete,
  DriveFileRenameOutline,
  Download,
  ContentCut,
  ContentCopy,
  ContentPaste,
  Refresh,
  ViewList,
  ViewModule,
  Folder,
  FolderZip,
  SelectAll,
  Deselect,
  SwapVert,
  ArrowUpward,
  ArrowDownward,
  Sort,
  NoteAdd,
  FileCopy,
  InfoOutlined,
  Search as SearchIcon,
  Unarchive,
  Edit as EditIcon,
  Visibility,
} from '@mui/icons-material'
import api from '../api/client'
import { usePortalStore } from '../stores/portalStore'
import { useFilesStore } from '../stores/filesStore'
import UploadDialog, { type UploadItem, traverseEntry } from '../components/UploadDialog'
import FileIcon from '../components/FileIcon'

function PreviewImageContent({ path, name }: { path: string; name: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let url: string | null = null
    let cancelled = false
    api.get('/files/download', { params: { path }, responseType: 'blob' })
      .then((res) => {
        if (cancelled) return
        url = URL.createObjectURL(res.data)
        setBlobUrl(url)
      })
      .catch(() => { if (!cancelled) setError(true) })
    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [path])

  if (error) return <Typography color="error">Error loading image</Typography>
  if (!blobUrl) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>
  return (
    <Box sx={{ textAlign: 'center' }}>
      <img src={blobUrl} alt={name} style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain' }} />
    </Box>
  )
}

function PreviewTextContent({ path }: { path: string }) {
  const { t } = useTranslation()
  const { data, isLoading, error } = useQuery<{ content: string }>({
    queryKey: ['files', 'read', path],
    queryFn: async () => {
      const res = await api.get('/files/read', { params: { path } })
      return res.data
    },
    enabled: !!path,
  })
  if (isLoading) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>
  if (error) return <Typography color="error">{t('files.readError') || 'Error reading file'}</Typography>
  return (
    <TextField
      multiline
      fullWidth
      minRows={10}
      maxRows={24}
      value={data?.content || ''}
      InputProps={{ readOnly: true }}
      sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
    />
  )
}

interface FileItem {
  name: string
  path: string
  type: 'file' | 'directory'
  size: number
  modified: string
  mime_type?: string
}

export default function MyCloudFilesPage() {
  const { t } = useTranslation()
  const { config } = usePortalStore()
  const filesStore = useFilesStore()
  const queryClient = useQueryClient()

  const glassmorphism = (config?.glassmorphism ?? false) && config?.backgroundMode === 'image'
  const glassSx = glassmorphism
    ? { bgcolor: 'rgba(255,255,255,0.65)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }
    : {}

  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [mkdirOpen, setMkdirOpen] = useState(false)
  const [mkdirName, setMkdirName] = useState('')
  const [renameOpen, setRenameOpen] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; item: FileItem } | null>(null)
  const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null)
  const [newfileOpen, setNewfileOpen] = useState(false)
  const [newfileName, setNewfileName] = useState('')
  const [infoOpen, setInfoOpen] = useState(false)
  const [infoData, setInfoData] = useState<{
    name?: string
    path?: string
    type?: string
    size?: number
    modified?: string
    mimeType?: string
    permissions?: string
    md5?: string
    sha256?: string
  } | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchMime, setSearchMime] = useState(false)
  const [searching, setSearching] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editPath, setEditPath] = useState('')
  const [editContent, setEditContent] = useState('')
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewItem, setPreviewItem] = useState<FileItem | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [newMenuAnchor, setNewMenuAnchor] = useState<HTMLElement | null>(null)

  const {
    data: listData,
    isLoading,
    error,
    refetch,
  } = useQuery<{ items: FileItem[]; path: string }>({
    queryKey: ['files', 'list', filesStore.currentPath],
    queryFn: async () => {
      const res = await api.get('/files/list', {
        params: { path: filesStore.currentPath },
      })
      return res.data
    },
  })

  const uploadMutation = useMutation({
    mutationFn: async (items: UploadItem[]) => {
      const formData = new FormData()
      for (const item of items) {
        const fileNameWithPath = item.relativePath || item.file.name
        formData.append('files', item.file, fileNameWithPath)
      }
      await api.post('/files/upload', formData, {
        params: { path: filesStore.currentPath },
      })
    },
    onSuccess: () => {
      setSnackbar({ message: t('files.uploadSuccess'), severity: 'success' })
      setUploadOpen(false)
      queryClient.invalidateQueries({ queryKey: ['files', 'list'] })
    },
    onError: (err: unknown) => {
      let detail = t('files.uploadError')
      if (err && typeof err === 'object' && 'response' in err) {
        const resp = (err as { response?: { data?: unknown } }).response
        if (resp?.data) {
          const d = resp.data
          if (typeof d === 'object' && d !== null && 'detail' in d) {
            detail = String((d as { detail: unknown }).detail)
          } else if (typeof d === 'string') {
            detail = d
          }
        }
      }
      setSnackbar({ message: detail, severity: 'error' })
    },
  })

  const mkdirMutation = useMutation({
    mutationFn: async () => {
      await api.post('/files/mkdir', {
        path: filesStore.currentPath,
        name: mkdirName,
      })
    },
    onSuccess: () => {
      setMkdirOpen(false)
      setMkdirName('')
      queryClient.invalidateQueries({ queryKey: ['files', 'list'] })
    },
  })

  const renameMutation = useMutation({
    mutationFn: async () => {
      const target = filesStore.selectedItems[0]
      await api.post('/files/rename', {
        path: target,
        newName: renameValue,
      })
    },
    onSuccess: () => {
      setRenameOpen(false)
      setRenameValue('')
      filesStore.clearSelection()
      queryClient.invalidateQueries({ queryKey: ['files', 'list'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.post('/files/delete', {
        paths: filesStore.selectedItems,
      })
    },
    onSuccess: () => {
      setConfirmDeleteOpen(false)
      filesStore.clearSelection()
      queryClient.invalidateQueries({ queryKey: ['files', 'list'] })
    },
  })

  const moveMutation = useMutation({
    mutationFn: async () => {
      if (!filesStore.clipboard) return
      await api.post('/files/move', {
        sourcePaths: filesStore.clipboard.paths,
        targetPath: filesStore.currentPath,
        isCut: filesStore.clipboard.isCut,
      })
    },
    onSuccess: () => {
      filesStore.clearClipboard()
      queryClient.invalidateQueries({ queryKey: ['files', 'list'] })
    },
  })

  const duplicateMutation = useMutation({
    mutationFn: async (itemPath: string) => {
      await api.post('/files/duplicate', {
        path: itemPath,
        targetPath: filesStore.currentPath,
      })
    },
    onSuccess: () => {
      filesStore.clearSelection()
      queryClient.invalidateQueries({ queryKey: ['files', 'list'] })
    },
  })

  const newfileMutation = useMutation({
    mutationFn: async () => {
      await api.post('/files/newfile', {
        path: filesStore.currentPath,
        name: newfileName,
      })
    },
    onSuccess: () => {
      setNewfileOpen(false)
      setNewfileName('')
      queryClient.invalidateQueries({ queryKey: ['files', 'list'] })
    },
  })

  const unzipMutation = useMutation({
    mutationFn: async (zipPath: string) => {
      await api.post('/files/unzip', { path: zipPath })
    },
    onSuccess: () => {
      setSnackbar({ message: t('files.unzipSuccess') || 'Extracted successfully', severity: 'success' })
      queryClient.invalidateQueries({ queryKey: ['files', 'list'] })
    },
    onError: () => {
      setSnackbar({ message: t('files.unzipError') || 'Error extracting ZIP', severity: 'error' })
    },
  })

  const saveContentMutation = useMutation({
    mutationFn: async () => {
      await api.post('/files/save', { path: editPath, content: editContent })
    },
    onSuccess: () => {
      setEditOpen(false)
      setSnackbar({ message: t('files.saveSuccess') || 'File saved', severity: 'success' })
      queryClient.invalidateQueries({ queryKey: ['files', 'list'] })
    },
  })

  const handleEdit = useCallback(async (itemPath: string) => {
    try {
      const res = await api.get('/files/read', { params: { path: itemPath } })
      setEditPath(itemPath)
      setEditContent(res.data.content || '')
      setEditOpen(true)
    } catch {
      setSnackbar({ message: t('files.readError') || 'Error reading file', severity: 'error' })
    }
  }, [t])

  const handlePreview = useCallback((item: FileItem) => {
    setPreviewItem(item)
    setPreviewOpen(true)
  }, [])

  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearching(false)
      setSearchQuery('')
      return
    }
    setSearchQuery(query)
    setSearching(true)
  }, [])

  const clearSearch = useCallback(() => {
    setSearchQuery('')
    setSearching(false)
  }, [])

  const {
    data: searchData,
    isLoading: searchLoading,
  } = useQuery<FileItem[]>({
    queryKey: ['files', 'search', filesStore.currentPath, searchQuery, searchMime],
    queryFn: async () => {
      const res = await api.get('/files/search', {
        params: { path: filesStore.currentPath, query: searchQuery, searchMime },
      })
      return res.data
    },
    enabled: searching && searchQuery.trim().length > 0,
  })

  const handleGetInfo = useCallback(async (itemPath: string) => {
    try {
      const res = await api.get('/files/info', { params: { path: itemPath } })
      setInfoData(res.data)
      setInfoOpen(true)
    } catch {
      setSnackbar({ message: t('files.infoError') || 'Failed to get file info', severity: 'error' })
    }
  }, [t])

  const handleDownload = useCallback(async () => {
    const selected = filesStore.selectedItems
    if (selected.length === 0) return

    try {
      let blob: Blob
      let filename: string

      if (selected.length === 1) {
        const item = listData?.items.find((i) => i.path === selected[0])
        if (!item) return

        if (item.type === 'directory') {
          const res = await api.get('/files/zip', {
            params: { paths: selected.join(',') },
            responseType: 'blob',
          })
          blob = res.data
          filename = `${item.name}.zip`
        } else {
          const res = await api.get('/files/download', {
            params: { path: item.path },
            responseType: 'blob',
          })
          blob = res.data
          filename = item.name

          const disposition = res.headers['content-disposition']
          if (disposition) {
            const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
            if (match?.[1]) filename = match[1].replace(/['"]/g, '')
          }
        }
      } else {
        const res = await api.get('/files/zip', {
          params: { paths: selected.join(',') },
          responseType: 'blob',
        })
        blob = res.data
        filename = 'download.zip'
      }

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch {
      setSnackbar({ message: t('files.uploadError'), severity: 'error' })
    }
  }, [filesStore.selectedItems, listData, t])

  const handleContextMenu = useCallback((e: React.MouseEvent, item: FileItem) => {
    e.preventDefault()
    e.stopPropagation()
    filesStore.clearSelection()
    filesStore.toggleSelection(item.path)
    setContextMenu({ x: e.clientX, y: e.clientY, item })
  }, [filesStore])

  const closeContextMenu = () => setContextMenu(null)

  const handleContextAction = (action: string) => {
    const item = contextMenu?.item
    if (!item) return
    closeContextMenu()

    switch (action) {
      case 'open':
        if (item.type === 'directory') filesStore.setCurrentPath(normalizePath(item.path))
        break
      case 'download':
        handleDownload()
        break
      case 'zip': {
        const paths = encodeURIComponent(item.path)
        api.get('/files/zip', { params: { paths }, responseType: 'blob' }).then((res) => {
          const url = URL.createObjectURL(res.data)
          const link = document.createElement('a')
          link.href = url
          link.download = `${item.name}.zip`
          document.body.appendChild(link)
          link.click()
          link.remove()
          URL.revokeObjectURL(url)
        })
        break
      }
      case 'cut':
        filesStore.cut([item.path])
        break
      case 'copy':
        filesStore.copy([item.path])
        break
      case 'move':
        filesStore.cut([item.path])
        setSnackbar({ message: t('files.moveHint') || 'Now navigate to the destination folder and paste', severity: 'success' })
        break
      case 'paste':
        moveMutation.mutate()
        break
      case 'rename':
        setRenameValue(item.name)
        setRenameOpen(true)
        break
      case 'delete':
        setConfirmDeleteOpen(true)
        break
      case 'selectAll':
        filesStore.selectAll(allPaths)
        break
      case 'selectNone':
        filesStore.clearSelection()
        break
      case 'invertSelection':
        filesStore.invertSelection(allPaths)
        break
      case 'duplicate':
        duplicateMutation.mutate(item.path)
        break
      case 'info':
        handleGetInfo(item.path)
        break
      case 'unzip':
        unzipMutation.mutate(item.path)
        break
      case 'edit':
        handleEdit(item.path)
        break
      case 'preview':
        handlePreview(item)
        break
    }
  }

  const normalizePath = (p: string) => p.replace(/\\/g, '/')

  const breadcrumbParts = filesStore.currentPath
    ? normalizePath(filesStore.currentPath).split('/').filter(Boolean)
    : []

  const navigateToBreadcrumb = (index: number) => {
    const newPath = breadcrumbParts.slice(0, index + 1).join('/')
    filesStore.setCurrentPath(newPath)
  }

  const handleItemClick = (e: React.MouseEvent, item: FileItem) => {
    if (e.shiftKey) {
      filesStore.selectRange(item.path, sortedPaths)
    } else if (e.ctrlKey || e.metaKey) {
      filesStore.toggleSelection(item.path)
    } else if (item.type === 'directory') {
      filesStore.setCurrentPath(normalizePath(item.path))
    } else {
      filesStore.clearSelection()
      filesStore.toggleSelection(item.path)
    }
  }

  const handleItemDoubleClick = (item: FileItem) => {
    if (item.type === 'directory') {
      filesStore.setCurrentPath(normalizePath(item.path))
    }
  }

  const canRename = filesStore.selectedItems.length === 1
  const canDelete = filesStore.selectedItems.length > 0
  const canPaste = !!filesStore.clipboard && filesStore.clipboard.paths.length > 0

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true)
    }
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Only set isDragging false if leaving the drop zone entirely
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX
    const y = e.clientY
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragging(false)
    }
  }, [])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const newItems: UploadItem[] = []
    const entries = Array.from(e.dataTransfer.items)
      .map((item) => item.webkitGetAsEntry?.())
      .filter((entry): entry is FileSystemEntry => entry !== null)

    if (entries.length > 0) {
      for (const entry of entries) {
        const traversed = await traverseEntry(entry, '')
        newItems.push(...traversed)
      }
    } else {
      for (const file of Array.from(e.dataTransfer.files)) {
        newItems.push({ file, relativePath: file.name })
      }
    }

    if (newItems.length > 0) {
      await uploadMutation.mutateAsync(newItems)
    }
  }, [uploadMutation])

  const allPaths = listData?.items.map((i) => i.path) || []

  const sortedItems = [...(listData?.items || [])].sort((a, b) => {
    // Directories always first
    if (a.type !== b.type) return a.type === 'directory' ? -1 : 1
    const dir = filesStore.sortDir === 'asc' ? 1 : -1
    switch (filesStore.sortBy) {
      case 'size': return (a.size - b.size) * dir
      case 'modified': return (new Date(a.modified).getTime() - new Date(b.modified).getTime()) * dir
      case 'type': return (a.mime_type || '').localeCompare(b.mime_type || '') * dir || a.name.localeCompare(b.name) * dir
      default: return a.name.localeCompare(b.name) * dir
    }
  })

  const sortedPaths = sortedItems.map((i) => i.path)

  const toggleSort = (col: 'name' | 'size' | 'modified' | 'type') => {
    if (filesStore.sortBy === col) {
      filesStore.setSort(col, filesStore.sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      filesStore.setSort(col, 'asc')
    }
  }

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '-'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
  }

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString()
    } catch {
      return dateStr
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2" sx={{ flex: 1 }}>
          {t('navigation.files')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            placeholder={t('files.search') || 'Search...'}
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            sx={{
              width: 220,
              '& .MuiOutlinedInput-root': glassSx,
            }}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          {searching && (
            <>
              <Tooltip title={t('files.searchMime') || 'Search by MIME type'}>
                <ToggleButton
                  size="small"
                  value="mime"
                  selected={searchMime}
                  onChange={() => setSearchMime(!searchMime)}
                  sx={glassSx}
                >
                  <InfoOutlined fontSize="small" />
                </ToggleButton>
              </Tooltip>
              <Tooltip title={t('files.clearSearch') || 'Clear search'}>
                <IconButton size="small" onClick={clearSearch} sx={glassSx}>
                  <Deselect fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button variant="outlined" size="small" startIcon={<CloudUpload />} onClick={() => setUploadOpen(true)} sx={glassSx}>
          {t('files.upload')}
        </Button>
        <Button variant="outlined" size="small" startIcon={<CreateNewFolder />} onClick={(e) => setNewMenuAnchor(e.currentTarget)} sx={glassSx}>
          {t('files.new') || 'New'}
        </Button>
        <Button variant="outlined" size="small" startIcon={<Download />} onClick={() => handleDownload()} disabled={!canDelete} sx={glassSx}>
          {t('files.download')}
        </Button>
        <Button variant="outlined" size="small" startIcon={<DriveFileRenameOutline />} onClick={() => {
          const selected = filesStore.selectedItems[0]
          const item = listData?.items.find((i) => i.path === selected)
          if (item) setRenameValue(item.name)
          setRenameOpen(true)
        }} disabled={!canRename} sx={glassSx}>
          {t('files.rename')}
        </Button>
        <Button variant="outlined" size="small" startIcon={<Delete />} onClick={() => setConfirmDeleteOpen(true)} disabled={!canDelete} color="error" sx={glassSx}>
          {t('files.delete')}
        </Button>
        <Button variant="outlined" size="small" startIcon={<ContentCut />} onClick={() => filesStore.cut(filesStore.selectedItems)} disabled={!canDelete} sx={glassSx}>
          {t('files.cut')}
        </Button>
        <Button variant="outlined" size="small" startIcon={<ContentCopy />} onClick={() => filesStore.copy(filesStore.selectedItems)} disabled={!canDelete} sx={glassSx}>
          {t('files.copy')}
        </Button>
        <Button variant="outlined" size="small" startIcon={<ContentPaste />} onClick={() => moveMutation.mutate()} disabled={!canPaste} sx={glassSx}>
          {t('files.paste')}
        </Button>
        <Tooltip title={t('common.retry') || 'Refresh'}>
          <IconButton onClick={() => refetch()} size="small" sx={glassSx}>
            <Refresh />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('files.sort')}>
          <IconButton onClick={(e) => setSortAnchor(e.currentTarget)} size="small" sx={glassSx}>
            <Sort />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('files.selectAll')}>
          <IconButton onClick={() => filesStore.selectAll(allPaths)} size="small" sx={glassSx}>
            <SelectAll />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('files.selectNone')}>
          <IconButton onClick={() => filesStore.clearSelection()} size="small" sx={glassSx}>
            <Deselect />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('files.invertSelection')}>
          <IconButton onClick={() => filesStore.invertSelection(allPaths)} size="small" sx={glassSx}>
            <SwapVert />
          </IconButton>
        </Tooltip>
        <ToggleButtonGroup
          value={filesStore.viewMode}
          exclusive
          onChange={(_, value) => value && filesStore.setViewMode(value)}
          size="small"
          sx={{
            ml: 'auto',
            '& .MuiToggleButton-root': glassSx,
          }}
        >
          <ToggleButton value="list" aria-label="list view">
            <ViewList fontSize="small" />
          </ToggleButton>
          <ToggleButton value="grid" aria-label="grid view">
            <ViewModule fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component="button" underline="hover" color="inherit" onClick={() => filesStore.setCurrentPath('')}>
          {t('files.root') || 'Root'}
        </Link>
        {breadcrumbParts.map((part, index) => (
          <Link
            key={index}
            component="button"
            underline="hover"
            color={index === breadcrumbParts.length - 1 ? 'text.primary' : 'inherit'}
            onClick={() => navigateToBreadcrumb(index)}
          >
            {part}
          </Link>
        ))}
      </Breadcrumbs>

      {/* File content area with drag & drop */}
      <Box
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{ position: 'relative' }}
      >
        {isDragging && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(25, 118, 210, 0.12)',
              border: '3px dashed',
              borderColor: 'primary.main',
              borderRadius: 2,
              pointerEvents: 'none',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CloudUpload sx={{ fontSize: 64, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" color="primary.main">
                {t('files.dropFiles')}
              </Typography>
            </Box>
          </Box>
        )}

      {/* Error / Loading */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {(error as any) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {(error as any).message || t('common.error')}
        </Alert>
      )}

      {/* Search Results */}
      {searching && (
        <Paper sx={{ ...glassSx, p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('files.searchResults') || 'Search results'}{searchQuery && `: "${searchQuery}"`}
          </Typography>
          {searchLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
          ) : (searchData?.length ?? 0) === 0 ? (
            <Typography color="text.secondary">{t('files.noResults') || 'No results found'}</Typography>
          ) : (
            <List dense disablePadding>
              {(searchData || []).map((item) => (
                <ListItem
                  key={item.path}
                  onClick={() => {
                    if (item.type === 'directory') filesStore.setCurrentPath(normalizePath(item.path))
                  }}
                  sx={{ cursor: item.type === 'directory' ? 'pointer' : 'default', borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  secondaryAction={
                    <IconButton edge="end" size="small" onClick={() => handleGetInfo(item.path)}>
                      <InfoOutlined fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <FileIcon type={item.type} extension={item.name.split('.').pop()} size={24} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    secondary={`${formatSize(item.size)} - ${formatDate(item.modified)}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}

      {/* File List / Grid */}
      {!isLoading && !error && filesStore.viewMode === 'list' && (
        <TableContainer
          component={Paper}
          sx={glassSx}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={filesStore.selectedItems.length > 0 && filesStore.selectedItems.length === (listData?.items.length || 0)}
                    indeterminate={filesStore.selectedItems.length > 0 && filesStore.selectedItems.length < (listData?.items.length || 0)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        filesStore.selectAll(allPaths)
                      } else {
                        filesStore.clearSelection()
                      }
                    }}
                  />
                </TableCell>
                <TableCell onClick={() => toggleSort('name')} sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                  {t('files.fileName')}
                  {filesStore.sortBy === 'name' && (filesStore.sortDir === 'asc' ? <ArrowUpward fontSize="inherit" sx={{ ml: 0.5, verticalAlign: 'middle', fontSize: 14 }} /> : <ArrowDownward fontSize="inherit" sx={{ ml: 0.5, verticalAlign: 'middle', fontSize: 14 }} />)}
                </TableCell>
                <TableCell onClick={() => toggleSort('size')} sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                  {t('files.fileSize')}
                  {filesStore.sortBy === 'size' && (filesStore.sortDir === 'asc' ? <ArrowUpward fontSize="inherit" sx={{ ml: 0.5, verticalAlign: 'middle', fontSize: 14 }} /> : <ArrowDownward fontSize="inherit" sx={{ ml: 0.5, verticalAlign: 'middle', fontSize: 14 }} />)}
                </TableCell>
                <TableCell onClick={() => toggleSort('modified')} sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                  {t('files.fileModified')}
                  {filesStore.sortBy === 'modified' && (filesStore.sortDir === 'asc' ? <ArrowUpward fontSize="inherit" sx={{ ml: 0.5, verticalAlign: 'middle', fontSize: 14 }} /> : <ArrowDownward fontSize="inherit" sx={{ ml: 0.5, verticalAlign: 'middle', fontSize: 14 }} />)}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">{t('files.empty')}</Typography>
                  </TableCell>
                </TableRow>
              )}
              {sortedItems.map((item) => (
                <TableRow
                  key={item.path}
                  hover
                  selected={filesStore.selectedItems.includes(item.path)}
                  onClick={(e) => handleItemClick(e, item)}
                  onDoubleClick={() => handleItemDoubleClick(item)}
                  onContextMenu={(e) => handleContextMenu(e, item)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={filesStore.selectedItems.includes(item.path)}
                      onChange={() => filesStore.toggleSelection(item.path)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FileIcon type={item.type} extension={item.name.split('.').pop()} size={24} />
                      {item.name}
                    </Box>
                  </TableCell>
                  <TableCell>{formatSize(item.size)}</TableCell>
                  <TableCell>{formatDate(item.modified)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Grid View */}
      {!isLoading && !error && filesStore.viewMode === 'grid' && (
        <Paper
          sx={{
            ...glassSx,
            p: 1.5,
          }}
        >
          {sortedItems.length === 0 && (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('files.empty')}</Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(3, 1fr)',
                sm: 'repeat(4, 1fr)',
                md: 'repeat(6, 1fr)',
                lg: 'repeat(8, 1fr)',
                xl: 'repeat(10, 1fr)',
              },
            }}
          >
            {sortedItems.map((item) => {
              const selected = filesStore.selectedItems.includes(item.path)
              const ext = item.name.includes('.') ? item.name.split('.').pop()! : undefined
              return (
                <Box
                  key={item.path}
                  onClick={(e) => handleItemClick(e, item)}
                  onDoubleClick={() => handleItemDoubleClick(item)}
                  onContextMenu={(e) => handleContextMenu(e, item)}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    pt: 1,
                    pb: 1,
                    px: 0.5,
                    borderRadius: 2,
                    bgcolor: selected ? 'action.selected' : 'transparent',
                    '&:hover': {
                      bgcolor: selected ? 'action.selected' : 'action.hover',
                    },
                    transition: 'background-color 150ms ease',
                  }}
                >
                  <Box sx={{ position: 'relative', lineHeight: 0 }}>
                    <Checkbox
                      size="small"
                      checked={selected}
                      onChange={(e) => {
                        e.stopPropagation()
                        filesStore.toggleSelection(item.path)
                      }}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        position: 'absolute',
                        top: -4,
                        left: -4,
                        p: 0,
                        opacity: selected ? 1 : 0,
                        transition: 'opacity 150ms ease',
                        zIndex: 1,
                        '&:hover': { opacity: '1 !important' },
                      }}
                    />
                    <FileIcon type={item.type} extension={ext} size={40} />
                  </Box>
                  <Typography
                    variant="caption"
                    align="center"
                    sx={{
                      mt: 0.5,
                      maxWidth: 90,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineHeight: 1.3,
                      fontSize: '0.7rem',
                    }}
                  >
                    {item.name}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </Paper>
      )}
      </Box>

      {/* Upload Dialog */}
      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUpload={async (items) => {
          await uploadMutation.mutateAsync(items)
        }}
      />

      {/* New Folder Dialog */}
      <Dialog open={mkdirOpen} onClose={() => setMkdirOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('files.newFolder')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('files.folderName')}
            fullWidth
            value={mkdirName}
            onChange={(e) => setMkdirName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMkdirOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={() => mkdirMutation.mutate()} variant="contained" disabled={!mkdirName.trim()}>
            {t('files.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('files.rename')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('files.fileName')}
            fullWidth
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={() => renameMutation.mutate()} variant="contained" disabled={!renameValue.trim()}>
            {t('files.rename')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('files.delete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('files.confirmDelete')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={() => deleteMutation.mutate()} variant="contained" color="error">
            {t('files.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New File Dialog */}
      <Dialog open={newfileOpen} onClose={() => setNewfileOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('files.newFile') || 'New File'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('files.fileName')}
            fullWidth
            value={newfileName}
            onChange={(e) => setNewfileName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && newfileName.trim() && newfileMutation.mutate()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewfileOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={() => newfileMutation.mutate()} variant="contained" disabled={!newfileName.trim()}>
            {t('files.create') || 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Info Dialog */}
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('files.info') || 'File Info'}</DialogTitle>
        <DialogContent>
          {infoData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('files.fileName') || 'Name'}</Typography>
                <Typography variant="body2">{infoData.name || ''}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('files.filePath') || 'Path'}</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{infoData.path || ''}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('files.fileType') || 'Type'}</Typography>
                <Typography variant="body2">{infoData.type === 'directory' ? (t('files.folder') || 'Folder') : (infoData.mimeType || 'file')}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('files.fileSize') || 'Size'}</Typography>
                <Typography variant="body2">{formatSize(infoData.size || 0)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('files.fileModified') || 'Modified'}</Typography>
                <Typography variant="body2">{formatDate(infoData.modified || '')}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('files.permissions') || 'Permissions'}</Typography>
                <Typography variant="body2">{infoData.permissions || ''}</Typography>
              </Box>
              {infoData.md5 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">MD5</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>{infoData.md5}</Typography>
                </Box>
              )}
              {infoData.sha256 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">SHA-256</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>{infoData.sha256}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)}>{t('common.close') || 'Close'}</Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        open={!!contextMenu}
        onClose={closeContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.y, left: contextMenu.x } : undefined}
      >
        {contextMenu?.item.type === 'directory' && [
          <MenuItem key="open" onClick={() => handleContextAction('open')}>
            <ListItemIcon><Folder fontSize="small" /></ListItemIcon>
            <ListItemText>{t('files.open') || 'Open'}</ListItemText>
          </MenuItem>,
          <Divider key="div1" />,
        ]}
        <MenuItem onClick={() => handleContextAction('download')}>
          <ListItemIcon><Download fontSize="small" /></ListItemIcon>
          <ListItemText>{t('files.download')}</ListItemText>
        </MenuItem>
        {contextMenu?.item.type === 'directory' && (
          <MenuItem onClick={() => handleContextAction('zip')}>
            <ListItemIcon><FolderZip fontSize="small" /></ListItemIcon>
            <ListItemText>{t('files.downloadZip') || 'Download as ZIP'}</ListItemText>
          </MenuItem>
        )}
        {contextMenu?.item.name?.toLowerCase().endsWith('.zip') && (
          <MenuItem onClick={() => handleContextAction('unzip')}>
            <ListItemIcon><Unarchive fontSize="small" /></ListItemIcon>
            <ListItemText>{t('files.unzip') || 'Extract ZIP'}</ListItemText>
          </MenuItem>
        )}
        {contextMenu?.item.type === 'file' && (
          <MenuItem onClick={() => handleContextAction('edit')}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('files.edit') || 'Edit'}</ListItemText>
          </MenuItem>
        )}
        {contextMenu?.item.type === 'file' && (
          <MenuItem onClick={() => handleContextAction('preview')}>
            <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
            <ListItemText>{t('files.preview') || 'Preview'}</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => handleContextAction('cut')}>
          <ListItemIcon><ContentCut fontSize="small" /></ListItemIcon>
          <ListItemText>{t('files.cut')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleContextAction('copy')}>
          <ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon>
          <ListItemText>{t('files.copy')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleContextAction('move')}>
          <ListItemIcon><ContentPaste fontSize="small" /></ListItemIcon>
          <ListItemText>{t('files.move')}</ListItemText>
        </MenuItem>
        {canPaste && (
          <MenuItem onClick={() => handleContextAction('paste')}>
            <ListItemIcon><ContentPaste fontSize="small" /></ListItemIcon>
            <ListItemText>{t('files.paste')}</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => handleContextAction('duplicate')}>
          <ListItemIcon><FileCopy fontSize="small" /></ListItemIcon>
          <ListItemText>{t('files.duplicate') || 'Duplicate'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleContextAction('rename')}>
          <ListItemIcon><DriveFileRenameOutline fontSize="small" /></ListItemIcon>
          <ListItemText>{t('files.rename')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleContextAction('delete')} sx={{ color: 'error.main' }}>
          <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>{t('files.delete')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleContextAction('info')}>
          <ListItemIcon><InfoOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>{t('files.info') || 'Info'}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleContextAction('selectAll')}>
          <ListItemIcon><SelectAll fontSize="small" /></ListItemIcon>
          <ListItemText>{t('files.selectAll')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleContextAction('selectNone')}>
          <ListItemIcon><Deselect fontSize="small" /></ListItemIcon>
          <ListItemText>{t('files.selectNone')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleContextAction('invertSelection')}>
          <ListItemIcon><SwapVert fontSize="small" /></ListItemIcon>
          <ListItemText>{t('files.invertSelection')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Sort Menu */}
      <Menu
        open={!!sortAnchor}
        onClose={() => setSortAnchor(null)}
        anchorEl={sortAnchor}
      >
        {(['name', 'size', 'modified'] as const).map((col) => (
          <MenuItem
            key={col}
            onClick={() => {
              toggleSort(col)
              setSortAnchor(null)
            }}
            selected={filesStore.sortBy === col}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {filesStore.sortBy === col && (filesStore.sortDir === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />)}
            </ListItemIcon>
            <ListItemText>{t(`files.sort${col.charAt(0).toUpperCase() + col.slice(1)}` as 'files.sortName' | 'files.sortSize' | 'files.sortModified')}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* New Menu */}
      <Menu
        open={!!newMenuAnchor}
        onClose={() => setNewMenuAnchor(null)}
        anchorEl={newMenuAnchor}
      >
        <MenuItem onClick={() => { setMkdirOpen(true); setNewMenuAnchor(null) }}>
          <ListItemIcon><CreateNewFolder fontSize="small" /></ListItemIcon>
          <ListItemText>{t('files.newFolder')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setNewfileOpen(true); setNewMenuAnchor(null) }}>
          <ListItemIcon><NoteAdd fontSize="small" /></ListItemIcon>
          <ListItemText>{t('files.newFile') || 'New File'}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('files.edit') || 'Edit'} — {editPath.split('/').pop() || editPath}</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            fullWidth
            minRows={12}
            maxRows={24}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            sx={{ fontFamily: 'monospace', '& .MuiInputBase-root': { fontFamily: 'monospace' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={() => saveContentMutation.mutate()} variant="contained" disabled={saveContentMutation.isPending}>
            {t('files.save') || 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{previewItem?.name || ''}</DialogTitle>
        <DialogContent>
          {previewItem && (() => {
            const ext = previewItem.name.split('.').pop()?.toLowerCase() || ''
            const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg']
            const textExts = ['txt', 'md', 'csv', 'json', 'xml', 'html', 'css', 'js', 'ts', 'py', 'sh', 'yaml', 'yml', 'ini', 'cfg', 'log', 'env']

            if (imageExts.includes(ext)) {
              return <PreviewImageContent path={previewItem.path} name={previewItem.name} />
            }
            if (textExts.includes(ext) || previewItem.size < 100000) {
              return <PreviewTextContent path={previewItem.path} />
            }
            return (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                {t('files.previewNotAvailable') || 'Preview not available for this file type'}
              </Typography>
            )
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>{t('common.close') || 'Close'}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)}>
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  )
}
